/**
 * 浏览器端语音转文字(基于 Web Speech API / webkitSpeechRecognition)
 *
 * 优势:
 *  - 零成本、零密钥
 *  - 中文普通话准确率 95%+(Chrome/Edge 桌面 + Android)
 *  - 浏览器本地转写,我们只把转写出的文本走 API,原始音频不外发(隐私友好)
 *
 * 限制:
 *  - Safari iOS / 微信内置浏览器不支持,返回 unsupported
 *  - 必须 https 或 localhost(浏览器限制)
 *  - 需要用户授权麦克风
 *
 * 用法:调用方用 useSpeechToText 拿到 start/stop/text/error/isSupported
 */

export type SpeechStatus = 'idle' | 'recording' | 'unsupported';

export interface UseSpeechToText {
  status: SpeechStatus;
  text: string;          // 当前已识别文本(final + interim)
  error: string | null;
  start: () => void;
  stop: () => void;
  reset: () => void;
  isSupported: boolean;
}

// ---------------- 浏览器侧类型(无 React 依赖) ----------------
interface SpeechRecognitionResultItem {
  transcript: string;
  confidence: number;
}
interface SpeechRecognitionResult {
  isFinal: boolean;
  [index: number]: SpeechRecognitionResultItem;
  length: number;
}
interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}
export interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((this: SpeechRecognitionInstance, ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((this: SpeechRecognitionInstance, ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: ((this: SpeechRecognitionInstance, ev: Event) => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

export function isSpeechRecognitionSupported(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return Boolean(w.SpeechRecognition ?? w.webkitSpeechRecognition);
}

export function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function explainSpeechError(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return '麦克风权限被拒绝,请在浏览器设置中允许';
    case 'no-speech':
      return '没听到声音,再试一次或靠近麦克风';
    case 'audio-capture':
      return '没有可用的麦克风';
    case 'network':
      return '网络问题,语音识别需要联网(Google 服务)';
    case 'aborted':
      return '已取消';
    default:
      return `语音识别失败(${code || '未知错误'})`;
  }
}
